import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/Tabs'
import { SkillToolEditor } from './SkillToolEditor'
import { SkillScriptEditor } from './SkillScriptEditor'
import {
  skillsService,
  type SkillBuilderRequest,
  type SkillFullResponse,
  type ScriptDef,
  type CredentialFieldDef,
  type ConfigFieldDef,
} from '../../services/skills'
import { useToast } from '../../hooks/useToast'
import {
  Loader2, Settings, FileText, Wrench, Code, KeyRound, SlidersHorizontal, Plus, Trash2, RefreshCw
} from 'lucide-react'

const CATEGORY_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'sales', label: 'Sales' },
  { value: 'utility', label: 'Utility' },
  { value: 'engagement', label: 'Engagement' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'productivity', label: 'Productivity' },
  { value: 'analytics', label: 'Analytics' },
]

interface SkillBuilderProps {
  isOpen: boolean
  onClose: () => void
  onSaved: () => void
  skillId?: string | null  // null = create mode, string = edit mode
  initialData?: Partial<SkillFullResponse> | null  // Pre-loaded data for edit mode
  chatbotVariables?: Record<string, any>  // From chatbot.settings.variables — shown as hints
}

/** Inline hint showing available session variables for credentials/scripts/tools tabs. */
const AvailableVariablesHint: React.FC<{
  variables: Record<string, any>
  mode: 'template' | 'env'  // template → {{variables.X}}, env → VAR_X
}> = ({ variables, mode }) => {
  const keys = Object.keys(variables)
  if (keys.length === 0) return null

  const [expanded, setExpanded] = React.useState(false)

  const copyToClipboard = (key: string) => {
    const text = mode === 'env' ? `VAR_${key.toUpperCase()}` : `{{variables.${key}}}`
    navigator.clipboard.writeText(text).catch(() => {})
  }

  return (
    <div className="mb-3 rounded-lg border border-blue-500/20 bg-blue-500/5 p-2.5">
      <button
        type="button"
        className="flex items-center gap-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 w-full text-left"
        onClick={() => setExpanded(!expanded)}
      >
        <span className="text-[10px]">{expanded ? '▼' : '▶'}</span>
        Available Variables ({keys.length})
      </button>
      {expanded && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {keys.map(key => {
            const display = mode === 'env' ? `VAR_${key.toUpperCase()}` : `{{variables.${key}}}`
            return (
              <button
                key={key}
                type="button"
                onClick={() => copyToClipboard(key)}
                className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono bg-blue-500/10 text-blue-700 dark:text-blue-300 hover:bg-blue-500/20 transition-colors"
                title={`Click to copy: ${display}`}
              >
                {display}
              </button>
            )
          })}
          <span className="text-[10px] text-muted-foreground self-center ml-1">(click to copy)</span>
        </div>
      )}
    </div>
  )
}

const asArray = <T,>(value: unknown): T[] => (Array.isArray(value) ? value as T[] : [])

const unwrapSkillPayload = (raw: unknown): Partial<SkillFullResponse> | null => {
  if (!raw || typeof raw !== 'object') return null

  const root = raw as Record<string, unknown>
  const candidates: Record<string, unknown>[] = [
    root,
    (root.data && typeof root.data === 'object' ? root.data : null) as Record<string, unknown>,
    (root.skill && typeof root.skill === 'object' ? root.skill : null) as Record<string, unknown>,
    (root.result && typeof root.result === 'object' ? root.result : null) as Record<string, unknown>,
  ].filter(Boolean)

  for (const candidate of candidates) {
    const hasUsableFields =
      typeof candidate.id === 'string' ||
      typeof candidate.name === 'string' ||
      typeof candidate.description === 'string' ||
      typeof candidate.content === 'string' ||
      Array.isArray(candidate.tools) ||
      Array.isArray(candidate.tags)

    if (hasUsableFields) return candidate as Partial<SkillFullResponse>
  }

  return null
}

export const SkillBuilder: React.FC<SkillBuilderProps> = ({
  isOpen,
  onClose,
  onSaved,
  skillId,
  initialData,
  chatbotVariables = {},
}) => {
  const { toast } = useToast()
  const toastRef = useRef(toast)
  toastRef.current = toast
  const normalizedInitialData = unwrapSkillPayload(initialData)
  const [isLoading, setIsLoading] = useState(Boolean(skillId && !normalizedInitialData))
  const [loadError, setLoadError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [activeTab, setActiveTab] = useState('basic')

  // Form state — initialized from initialData if provided (edit mode)
  const [name, setName] = useState(normalizedInitialData?.name || '')
  const [description, setDescription] = useState(normalizedInitialData?.description || '')
  const [content, setContent] = useState(normalizedInitialData?.content || '')
  const [category, setCategory] = useState(normalizedInitialData?.category || '')
  const [tags, setTags] = useState(asArray<string>(normalizedInitialData?.tags).join(', '))
  const [visibility, setVisibility] = useState(normalizedInitialData?.visibility || 'private')
  const [tools, setTools] = useState<any[]>(asArray<any>(normalizedInitialData?.tools))
  const [scripts, setScripts] = useState<ScriptDef[]>(asArray<ScriptDef>(normalizedInitialData?.scripts))
  const [credentials, setCredentials] = useState<CredentialFieldDef[]>(
    asArray<any>(normalizedInitialData?.credentials_schema).map(c => ({
      name: c.name || '', type: c.type || 'string', label: c.label || '', required: c.required !== false,
    }))
  )
  const [config, setConfig] = useState<ConfigFieldDef[]>(
    asArray<any>(normalizedInitialData?.config_schema).map(c => ({
      name: c.name || '', type: c.type || 'string', label: c.label || '', default: c.default || '', options: c.options || [],
    }))
  )

  const applySkillData = useCallback((data: Partial<SkillFullResponse>) => {
    setName(data.name || '')
    setDescription(data.description || '')
    setContent(data.content || '')
    setCategory(data.category || '')
    setTags(asArray<string>(data.tags).join(', '))
    setVisibility(data.visibility || 'private')
    setTools(asArray<any>(data.tools))
    setScripts(asArray<ScriptDef>(data.scripts))
    setCredentials(asArray<any>(data.credentials_schema).map(c => ({
      name: c.name || '',
      type: c.type || 'string',
      label: c.label || '',
      required: c.required !== false,
    })))
    setConfig(asArray<any>(data.config_schema).map(c => ({
      name: c.name || '',
      type: c.type || 'string',
      label: c.label || '',
      default: c.default || '',
      options: c.options || [],
    })))
  }, [])

  const loadSkill = useCallback(async () => {
    if (!skillId) return

    setIsLoading(true)
    try {
      const raw = await skillsService.getFullSkill(skillId)
      const data = unwrapSkillPayload(raw)
      if (!data) {
        throw new Error('Invalid skill payload returned by server')
      }
      applySkillData(data)
      setLoadError(null)
    } catch (err: any) {
      const msg = err.response?.data?.detail || err.message || 'Unknown error'
      setLoadError(msg)
      toastRef.current({ title: `Failed to load skill: ${msg}`, variant: 'destructive' })
    } finally {
      setIsLoading(false)
    }
  }, [skillId, applySkillData])

  useEffect(() => {
    if (!normalizedInitialData) return
    applySkillData(normalizedInitialData)
    setLoadError(null)
  }, [normalizedInitialData, applySkillData])

  // Fallback: fetch skill data if no preloaded edit payload is available.
  useEffect(() => {
    if (!skillId || normalizedInitialData) return
    loadSkill()
  }, [skillId, normalizedInitialData, loadSkill])

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' })
      return
    }
    if (!description.trim()) {
      toast({ title: 'Description is required', variant: 'destructive' })
      return
    }

    const body: SkillBuilderRequest = {
      name: name.trim(),
      description: description.trim(),
      content: content.trim(),
      category: category,
      tags: tags.split(',').map(t => t.trim()).filter(Boolean),
      visibility,
      tools,
      scripts,
      credentials,
      config,
      references: [],
    }

    setIsSaving(true)
    try {
      if (skillId) {
        await skillsService.updateFromBuilder(skillId, body)
        toast({ title: 'Skill updated', variant: 'default' })
      } else {
        await skillsService.createFromBuilder(body)
        toast({ title: 'Skill created', variant: 'default' })
      }
      onSaved()
      onClose()
    } catch (err: any) {
      const detail = err.response?.data?.detail || err.message
      toast({ title: `Save failed: ${detail}`, variant: 'destructive' })
    } finally {
      setIsSaving(false)
    }
  }

  // Credential row helpers
  const addCredential = () => {
    setCredentials([...credentials, { name: '', type: 'string', label: '', required: true }])
  }
  const updateCredential = (i: number, updates: Partial<CredentialFieldDef>) => {
    setCredentials(credentials.map((c, idx) => idx === i ? { ...c, ...updates } : c))
  }
  const removeCredential = (i: number) => {
    setCredentials(credentials.filter((_, idx) => idx !== i))
  }

  // Config row helpers
  const addConfigField = () => {
    setConfig([...config, { name: '', type: 'string', label: '', default: '', options: [] }])
  }
  const updateConfigField = (i: number, updates: Partial<ConfigFieldDef>) => {
    setConfig(config.map((c, idx) => idx === i ? { ...c, ...updates } : c))
  }
  const removeConfigField = (i: number) => {
    setConfig(config.filter((_, idx) => idx !== i))
  }

  const TAB_ITEMS = [
    { id: 'basic', label: 'Basic', icon: Settings },
    { id: 'instructions', label: 'Instructions', icon: FileText },
    { id: 'tools', label: 'Tools', icon: Wrench },
    { id: 'scripts', label: 'Scripts', icon: Code },
    { id: 'credentials', label: 'Credentials', icon: KeyRound },
    { id: 'config', label: 'Config', icon: SlidersHorizontal },
  ]

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={skillId ? 'Edit Skill' : 'Create Skill'}
      size="xl"
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        </div>
      ) : loadError ? (
        <div className="flex flex-col items-center justify-center py-16 gap-4">
          <p className="text-sm text-red-600">Failed to load skill: {loadError}</p>
          <Button variant="outline" size="sm" onClick={() => {
            setLoadError(null)
            loadSkill()
          }}>
            <RefreshCw className="w-4 h-4 mr-1" /> Retry
          </Button>
        </div>
      ) : (
        <div className="flex flex-col h-[70vh]">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="border-b border-border bg-transparent px-0 -mx-1">
              {TAB_ITEMS.map(tab => (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="text-sm data-[state=active]:border-foreground"
                >
                  <tab.icon className="w-4 h-4 mr-1.5" />
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <div className="flex-1 overflow-y-auto py-5 min-h-0">
              <div className="px-1 pr-2">
                {/* Basic tab */}
                <TabsContent value="basic" className="mt-0 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Name
                    </label>
                    <Input
                      placeholder="e.g. weather-lookup"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Description
                    </label>
                    <textarea
                      className="w-full min-h-[80px] px-3.5 py-2.5 text-sm border border-border rounded-xl bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      placeholder="What does this skill do?"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Category
                      </label>
                      <select
                        className="w-full h-10 px-3 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                      >
                        {CATEGORY_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Visibility
                      </label>
                      <select
                        className="w-full h-10 px-3 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer"
                        value={visibility}
                        onChange={(e) => setVisibility(e.target.value)}
                      >
                        <option value="private">Private</option>
                        <option value="shared">Shared</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Tags (comma-separated)
                    </label>
                    <Input
                      placeholder="e.g. weather, api, utility"
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                    />
                  </div>
                </TabsContent>

                {/* Instructions tab */}
                <TabsContent value="instructions" className="mt-0 space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Markdown instructions for the AI agent. This becomes the SKILL.md body.
                  </p>
                  <textarea
                    className="w-full min-h-[350px] px-3.5 py-2.5 text-sm font-mono border border-border rounded-xl bg-muted text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="## Instructions&#10;&#10;Describe how the agent should use this skill..."
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                  />
                  <div className="flex justify-end">
                    <span className="text-xs text-muted-foreground">
                      {content.split(/\s+/).filter(Boolean).length} words
                    </span>
                  </div>
                </TabsContent>

                {/* Tools tab */}
                <TabsContent value="tools" className="mt-0">
                  <AvailableVariablesHint variables={chatbotVariables} mode="template" />
                  <SkillToolEditor tools={tools} onChange={setTools} />
                </TabsContent>

                {/* Scripts tab */}
                <TabsContent value="scripts" className="mt-0">
                  <AvailableVariablesHint variables={chatbotVariables} mode="env" />
                  <SkillScriptEditor scripts={scripts} onChange={setScripts} />
                </TabsContent>

                {/* Credentials tab */}
                <TabsContent value="credentials" className="mt-0 space-y-4">
                  <AvailableVariablesHint variables={chatbotVariables} mode="template" />
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      Define fields that bot owners fill in when enabling this skill (e.g. API keys).
                    </p>
                    <Button type="button" variant="outline" size="sm" onClick={addCredential}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add Field
                    </Button>
                  </div>
                  {credentials.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground border-2 border-dashed border-border rounded-xl">
                      <KeyRound className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No credential fields. Add one if the skill needs API keys.</p>
                    </div>
                  )}
                  {credentials.map((cred, i) => (
                    <div key={i} className="flex items-center gap-2 p-3 rounded-xl border border-border">
                      <Input
                        className="flex-1"
                        placeholder="Field name (e.g. api_key)"
                        value={cred.name}
                        onChange={(e) => updateCredential(i, { name: e.target.value })}
                      />
                      <Input
                        className="flex-1"
                        placeholder="Label (e.g. API Key)"
                        value={cred.label}
                        onChange={(e) => updateCredential(i, { label: e.target.value })}
                      />
                      <label className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={cred.required}
                          onChange={(e) => updateCredential(i, { required: e.target.checked })}
                        />
                        Required
                      </label>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeCredential(i)}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  ))}
                </TabsContent>

                {/* Config tab */}
                <TabsContent value="config" className="mt-0 space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      Define configurable settings for this skill (e.g. thresholds, modes).
                    </p>
                    <Button type="button" variant="outline" size="sm" onClick={addConfigField}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add Field
                    </Button>
                  </div>
                  {config.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground border-2 border-dashed border-border rounded-xl">
                      <SlidersHorizontal className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No config fields.</p>
                    </div>
                  )}
                  {config.map((cf, i) => (
                    <div key={i} className="p-3 rounded-xl border border-border space-y-2">
                      <div className="flex items-center gap-2">
                        <Input
                          className="flex-1"
                          placeholder="Field name"
                          value={cf.name}
                          onChange={(e) => updateConfigField(i, { name: e.target.value })}
                        />
                        <select
                          className="h-10 w-28 px-2 text-sm border border-border rounded-lg bg-background text-foreground"
                          value={cf.type}
                          onChange={(e) => updateConfigField(i, { type: e.target.value })}
                        >
                          <option value="string">string</option>
                          <option value="number">number</option>
                          <option value="boolean">boolean</option>
                          <option value="select">select</option>
                        </select>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeConfigField(i)}>
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          className="flex-1"
                          placeholder="Label"
                          value={cf.label}
                          onChange={(e) => updateConfigField(i, { label: e.target.value })}
                        />
                        <Input
                          className="flex-1"
                          placeholder="Default value"
                          value={cf.default}
                          onChange={(e) => updateConfigField(i, { default: e.target.value })}
                        />
                      </div>
                    </div>
                  ))}
                </TabsContent>
              </div>
            </div>
          </Tabs>

          {/* Footer */}
          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button variant="outline" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSaving}>
              {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {skillId ? 'Save Changes' : 'Create Skill'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}

export default SkillBuilder
